import EventEmitter from 'eventemitter3';
import { Api, APIResponse, Fetch, Methods, RouteGeneratorFn } from './api';
import { AuthenticationError, PermissionDeniedError } from './error';

type ApiEventEmitter = EventEmitter<'session invalid'>;

export interface AuthFailureEmittingApi {
  emitter: ApiEventEmitter;
}

export class BlueJApi extends Api implements AuthFailureEmittingApi {
  public emitter: ApiEventEmitter;

  constructor(backendUrl = '', overriddenFetch?: Fetch | undefined) {
    super(backendUrl, overriddenFetch);
    this.emitter = new EventEmitter();
  }

  /**
   * The browser is responsible for the multipart Content-Type header and applying the correct "boundary" headers for
   * formData uploads
   */
  createFormDataFetch<TResponse, TParams = never>(apiSubRoute: (params?: TParams) => string) {
    return (payload: FormData, params?: TParams): APIResponse<TResponse> => {
      const url = `${this.backendUrl}${apiSubRoute(params)}`;

      const fetcher = fetch(url, {
        method: 'POST',
        body: payload,
        headers: {
          'X-Ask-Blue-J-Request': 'true'
        }
      }).then((response) => ({
        response
      }))

      const response = new APIResponse<TResponse>(fetcher);

      response.parsedResponse().catch((err) => {
        // Only emitting auth related events that are a result of having a bad session
        if (err instanceof AuthenticationError || err instanceof PermissionDeniedError && err.isRevoked()) {
          this.emitter.emit('session invalid', { err });
        }
      });

      return response;
    }
  }

  protected override createFetch<TPayload, TResponse, TParams>(apiSubRoute: string | RouteGeneratorFn<TParams>, method: Methods = 'POST'): (payload?: TPayload, params?: TParams) => APIResponse<TResponse> {
    const fetcher = super.createFetch<TPayload, TResponse, TParams>(apiSubRoute, method);

    return (payload?: TPayload, params?: TParams): APIResponse<TResponse> => {
      const response = fetcher(payload, params);
      response.parsedResponse().catch((err) => {
        // Only emitting auth related events that are a result of having a bad session
        if (err instanceof AuthenticationError || err instanceof PermissionDeniedError && err.isRevoked()) {
          this.emitter.emit('session invalid', { err });
        }
      });
      return response;
    }
  }
}
